import React, { Suspense } from 'react';
import { Navigate, Route } from 'react-router-dom';

import ProtectedRoute from '../ProtectedRoute';
import Layout from '../Layout';
import Shimmer from '../Common/Shimmer';

// Shared pages also used by institute routes — imported here too so this module
// is self-contained. App.js keeps its own imports of these, so they remain in
// the enterprise bundle even when this module is stubbed out.
import SupportDeskPage from '../Support/SupportDeskPage';
import TicketDetailPage from '../Support/TicketDetailPage';
import AccountSettingsPage from '../Account/AccountSettingsPage';
import CodingQuestionBankPage from '../CodingQuestionBank/CodingQuestionBankPage';

// Marketplace authoring (library mode) — reuse institute pages; they self-detect
// the `/sa/` prefix and target the platform library (instituteId:null).
import QuizQuestionsPage from '../QuizBank/QuizQuestionsPage';
import AddEditQuizQuestionPage from '../QuizBank/AddEditQuizQuestionPage';
import PassagesPage from '../QuizBank/PassagesPage';
import PassageEditorPage from '../QuizBank/PassageEditorPage';
import QuizBulkUploadPage from '../QuizBank/BulkUploadPage';
import AssessmentsPage from '../Assessments/AssessmentsPage';
import AssessmentBuilderPage from '../Assessments/Builder/AssessmentBuilderPage';
import AssessmentPreviewPage from '../Assessments/AssessmentPreviewPage';


/**
 * Route-level code splitting for `/sa/*` — design-system Phase 7.2, decision D4(a).
 *
 * Every stylesheet in the app used to land in one `main.css`: 2.36 MB, 356 KB
 * gzipped, downloaded before the first paint of a public exam link opened on a
 * phone. `/sa/*` is the cleanest split point in the tree — 39 routes that only a
 * platform admin ever loads, already isolated in this one module so the
 * enterprise build can drop them.
 *
 * CRA's MiniCssExtractPlugin gives every async chunk its own `.chunk.css`, so
 * lazy-loading the page components moves their CSS out of `main.css` too. That
 * is the whole point: this is a CSS split executed through the JS graph, because
 * the CSS graph is the JS graph.
 *
 * ⚠️ A chunk stylesheet loads AFTER `main.css`, and `main.css` is where
 * `styles/tailwind.css` lives. Utilities win by source order (see
 * tailwind.config.js — there is deliberately no `important`), so a lazy module
 * that used BOTH `tw-` classes and its own stylesheet would have that order
 * inverted against it. Nothing under `SuperAdmin/` does: the lint's `mixed`
 * check makes a file one or the other, and every file here is SCSS. Check this
 * before lazy-loading a route group that has been through Phase 5.
 *
 * Shared pages are deliberately NOT lazy here: App.js imports them eagerly for
 * the institute routes, so they are in `main` regardless and a second, lazy
 * reference would only add a wrapper.
 */
function lazyPage(loader) {
  const Lazy = React.lazy(loader);
  return (props) => (
    <Suspense fallback={<div className="sa-dashboard"><Shimmer height={72} count={6} /></div>}>
      <Lazy {...props} />
    </Suspense>
  );
}

// ── SuperAdmin (Tenant Dashboard) control-plane pages ──
// This module is the SINGLE entry point for the SaaS platform super-admin UI.
// All `/sa/*` page imports live here (not in App.js) so that an enterprise
// build can tree-drop the entire control plane by aliasing this one module to
// SuperAdminRoutes.enterprise.js (see config-overrides.js).
const SADashboardPage = lazyPage(() => import('./Dashboard/SADashboardPage'));
const InstitutesListPage = lazyPage(() => import('./Institutes/InstitutesListPage'));
const InstituteDetailPage = lazyPage(() => import('./Institutes/InstituteDetailPage'));
const PlansPage = lazyPage(() => import('./Plans/PlansPage'));
const UsersPage = lazyPage(() => import('./Users/UsersPage'));
const AuditLogsPage = lazyPage(() => import('./AuditLogs/AuditLogsPage'));
const SASettingsPage = lazyPage(() => import('./Settings/SettingsPage'));
const SAWhatsAppConfigPage = lazyPage(() => import('./WhatsAppConfig/WhatsAppConfigPage'));
const SAZohoConfigPage = lazyPage(() => import('./ZohoConfig/ZohoConfigPage'));
const SAZohoMeetingConfigPage = lazyPage(() => import('./ZohoMeetingConfig/ZohoMeetingConfigPage'));
const SAGratsMeetConfigPage = lazyPage(() => import('./GratsMeetConfig/GratsMeetConfigPage'));
const PlatformWhatsAppInbox = lazyPage(() => import('./WhatsAppInbox/PlatformWhatsAppInbox'));
const ZoomHostsPage = lazyPage(() => import('./ZoomHosts/ZoomHostsPage'));
const PayoutsPage = lazyPage(() => import('./Payouts/PayoutsPage'));
const SettlementHistoryPage = lazyPage(() => import('./Payouts/SettlementHistoryPage'));
const SAInstitutesOverview = lazyPage(() => import('./Analytics/SAInstitutesOverview'));
const SAInstituteAnalytics = lazyPage(() => import('./Analytics/SAInstituteAnalytics'));
const FormTemplatesPage = lazyPage(() => import('./FormTemplates/FormTemplatesPage'));
const SAStoragePage = lazyPage(() => import('./Storage/SAStoragePage'));
const SAAIWhatsAppUsagePage = lazyPage(() => import('./AIWhatsApp/SAAIWhatsAppUsagePage'));
const SalesLeadsPage = lazyPage(() => import('./SalesLeads/SalesLeadsPage'));
const ServiceInvoicesPage = lazyPage(() => import('./ServiceInvoices/ServiceInvoicesPage'));
const ServiceInvoiceForm = lazyPage(() => import('./ServiceInvoices/ServiceInvoiceForm'));
const ServiceInvoiceDetail = lazyPage(() => import('./ServiceInvoices/ServiceInvoiceDetail'));
const RevenueSharePage = lazyPage(() => import('./RevenueShare/RevenueSharePage'));
const LicensesPage = lazyPage(() => import('./Licenses/LicensesPage'));
const WhatsNewPage = lazyPage(() => import('./WhatsNew/WhatsNewPage'));
const DistributorsListPage = lazyPage(() => import('./Distributors/DistributorsListPage'));
const DistributorNetworkPage = lazyPage(() => import('./Distributors/DistributorNetworkPage'));
const DistributorDetailPage = lazyPage(() => import('./Distributors/DistributorDetailPage'));

/**
 * Returns the `/sa/*` route subtree (a single <Route> element with nested
 * routes). Called inline inside <Routes> in App.js: `{renderSuperAdminRoutes()}`.
 * React Router v7 accepts a returned <Route> element exactly like an inline one.
 */
export function renderSuperAdminRoutes() {
  return (
    <Route element={<ProtectedRoute allowed={["tenantadmin"]} />}>
      <Route element={<Layout />}>
        <Route path="/sa" element={<SADashboardPage />} />
        <Route path="/sa/institutes" element={<InstitutesListPage />} />
        <Route path="/sa/institutes/:id" element={<InstituteDetailPage />} />
        <Route path="/sa/distributors" element={<DistributorsListPage />} />
        <Route path="/sa/distributors/network" element={<DistributorNetworkPage />} />
        <Route path="/sa/distributors/:id" element={<DistributorDetailPage />} />
        <Route path="/sa/plans" element={<PlansPage />} />
        <Route path="/sa/licenses" element={<LicensesPage />} />
        <Route path="/sa/zoom-hosts" element={<ZoomHostsPage />} />
        <Route path="/sa/users" element={<UsersPage />} />
        <Route path="/sa/audit-logs" element={<AuditLogsPage />} />
        <Route path="/sa/payouts" element={<PayoutsPage />} />
        <Route path="/sa/payouts/history" element={<SettlementHistoryPage />} />
        <Route path="/sa/revenue-share" element={<RevenueSharePage />} />
        <Route path="/sa/support" element={<SupportDeskPage scope="platform" />} />
        <Route path="/sa/support/:id" element={<TicketDetailPage backPath="/sa/support" backLabel="Support desk" />} />
        <Route path="/sa/settings" element={<SASettingsPage />} />
        <Route path="/sa/whatsapp" element={<SAWhatsAppConfigPage />} />
        <Route path="/sa/whatsapp-inbox" element={<PlatformWhatsAppInbox />} />
        <Route path="/sa/zoho" element={<SAZohoConfigPage />} />
        <Route path="/sa/zoho-meeting" element={<SAZohoMeetingConfigPage />} />
        <Route path="/sa/grats-meet" element={<SAGratsMeetConfigPage />} />
        <Route path="/sa/account-settings" element={<AccountSettingsPage />} />
        <Route path="/sa/analytics" element={<SAInstitutesOverview />} />
        <Route path="/sa/analytics/:id" element={<SAInstituteAnalytics />} />
        <Route path="/sa/form-templates" element={<FormTemplatesPage />} />
        <Route path="/sa/coding-questions" element={<CodingQuestionBankPage mode="library" />} />
        <Route path="/sa/storage" element={<SAStoragePage />} />
        <Route path="/sa/ai-whatsapp" element={<SAAIWhatsAppUsagePage />} />
        <Route path="/sa/sales-leads" element={<SalesLeadsPage />} />
        <Route path="/sa/service-invoices" element={<ServiceInvoicesPage />} />
        <Route path="/sa/service-invoices/create" element={<ServiceInvoiceForm />} />
        <Route path="/sa/service-invoices/edit/:id" element={<ServiceInvoiceForm />} />
        <Route path="/sa/service-invoices/:id" element={<ServiceInvoiceDetail />} />
        <Route path="/sa/whats-new" element={<WhatsNewPage />} />

        {/* ── Marketplace authoring (platform library) ── */}
        <Route path="/sa/marketplace/quiz-questions" element={<QuizQuestionsPage />} />
        <Route path="/sa/marketplace/quiz-questions/add" element={<AddEditQuizQuestionPage />} />
        <Route path="/sa/marketplace/quiz-questions/edit/:id" element={<AddEditQuizQuestionPage />} />
        <Route path="/sa/marketplace/quiz-questions/bulk-upload" element={<QuizBulkUploadPage />} />
        <Route path="/sa/marketplace/quiz-questions/categories" element={<Navigate to="/sa/marketplace/quiz-questions" replace />} />
        <Route path="/sa/marketplace/quiz-questions/passages" element={<PassagesPage />} />
        <Route path="/sa/marketplace/quiz-questions/passages/new" element={<PassageEditorPage />} />
        <Route path="/sa/marketplace/quiz-questions/passages/:id" element={<PassageEditorPage />} />
        <Route path="/sa/marketplace/assessments" element={<AssessmentsPage />} />
        <Route path="/sa/marketplace/assessments/create" element={<AssessmentBuilderPage />} />
        <Route path="/sa/marketplace/assessments/edit/:id" element={<AssessmentBuilderPage />} />
        <Route path="/sa/marketplace/assessments/:id/preview" element={<AssessmentPreviewPage />} />
      </Route>
    </Route>
  );
}
